import React, { useState, useEffect } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress, Switch, FormControlLabel } from '@mui/material';
import { WalteroCard, WalteroTypography } from '../waltero';
import { useSharedPerformanceCache } from '../../context/SharedPerformanceCache';

/**
 * Simple Temperature Table - Daily temperature readings
 * 
 * Shows daily minimum and maximum temperature values for all devices.
 */
const SimpleTemperatureTable = ({ selectedOrgId, startDate, endDate }) => {
  const [temperatureData, setTemperatureData] = useState({});
  const [isFahrenheit, setIsFahrenheit] = useState(false);
  
  const { 
    loadAllPerformanceData,
    getEndpointData,
    getDevices,
    cacheInfo,
    isEndpointLoading
  } = useSharedPerformanceCache();

  useEffect(() => {
    loadTemperatureData();
  }, [selectedOrgId, startDate, endDate]);

  // Update data whenever cache changes
  useEffect(() => {
    const devices = getDevices();
    const dataMap = {};
    
    devices.forEach(device => {
      const data = getEndpointData('temperature-daily', device.id);
      if (data) {
        dataMap[device.id] = data;
      }
    });
    
    setTemperatureData(dataMap);
  }, [cacheInfo.endpointsLoaded, getEndpointData, getDevices]);

  const loadTemperatureData = () => {
    if (!selectedOrgId || !startDate || !endDate) return;
    
    loadAllPerformanceData(selectedOrgId, startDate, endDate);
  };

  // Get devices from shared cache
  const devices = getDevices();

  // Generate all dates in the selected range (regardless of data availability)
  const generateDateRange = (start, end) => {
    const dates = [];
    const currentDate = new Date(start);
    const endDate = new Date(end);
    
    while (currentDate <= endDate) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  // Generate dates in reverse chronological order (most recent first)
  const sortedDates = startDate && endDate ? generateDateRange(startDate, endDate).sort((a, b) => b.localeCompare(a)) : [];

  // Temperature conversion functions
  const convertTemp = (celsius) => {
    if (celsius === undefined || celsius === null) return celsius;
    if (isFahrenheit) {
      return Math.round((celsius * 9/5 + 32) * 10) / 10; // Convert to F and round to 1 decimal
    }
    return celsius;
  };

  const getTempUnit = () => isFahrenheit ? '°F' : '°C';

  // Get temperature color based on fixed scale:
  // -5 to +10: Blue gradient (cold)
  // +10 to +40: Green (normal operating range)
  // +40 to +55: Red gradient (hot)
  const getTemperatureColor = (tempCelsius) => {
    if (tempCelsius === undefined || tempCelsius === null) return '#fafafa';

    if (tempCelsius < -5) {
      // Very cold - deep blue
      return 'rgba(13, 71, 161, 0.4)'; // Deep blue
    } else if (tempCelsius < 10) {
      // Cold - blue gradient
      const ratio = (tempCelsius + 5) / 15; // 0 at -5°C, 1 at 10°C
      // Interpolate from blue to green
      const r = Math.round(33 + (76 - 33) * ratio);
      const g = Math.round(150 + (175 - 150) * ratio);
      const b = Math.round(243 - (243 - 80) * ratio);
      const opacity = 0.3 - ratio * 0.1; // Fade from 0.3 to 0.2
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    } else if (tempCelsius <= 40) {
      // Normal range - green
      return 'rgba(76, 175, 80, 0.2)'; // Consistent green
    } else if (tempCelsius <= 55) {
      // Hot - red gradient
      const ratio = (tempCelsius - 40) / 15; // 0 at 40°C, 1 at 55°C
      // Interpolate from green to red
      const r = Math.round(76 + (244 - 76) * ratio);
      const g = Math.round(175 - (175 - 67) * ratio);
      const b = Math.round(80 - (80 - 54) * ratio);
      const opacity = 0.2 + ratio * 0.2; // Increase from 0.2 to 0.4
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    } else {
      // Very hot - deep red
      return 'rgba(198, 40, 40, 0.4)'; // Deep red
    }
  };

  // Get text color based on temperature
  const getTemperatureTextColor = (tempCelsius) => {
    if (tempCelsius === undefined || tempCelsius === null) return '#999';

    if (tempCelsius < -5) {
      return '#0d47a1'; // Deep blue
    } else if (tempCelsius < 10) {
      return '#1565c0'; // Medium blue
    } else if (tempCelsius <= 40) {
      return '#2e7d32'; // Dark green
    } else if (tempCelsius <= 55) {
      return '#d32f2f'; // Dark red
    } else {
      return '#b71c1c'; // Deep red
    }
  };

  // Generate color gradient for legend
  const generateColorGradient = () => {
    const stops = [];
    const numStops = 60; // Number of gradient stops

    for (let i = 0; i <= numStops; i++) {
      const temp = -5 + (i / numStops) * 60; // -5 to +55
      const color = getTemperatureColor(temp);
      const position = (i / numStops) * 100;
      stops.push(`${color} ${position}%`);
    }

    return `linear-gradient(to right, ${stops.join(', ')})`;
  };


  return (
    <WalteroCard sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <WalteroTypography variant="h6">
          Device Temperature - Daily Min/Max
        </WalteroTypography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isFahrenheit}
                onChange={(e) => setIsFahrenheit(e.target.checked)}
                color="primary"
              />
            }
            label={isFahrenheit ? 'Fahrenheit' : 'Celsius'}
          />
          {cacheInfo.isLoading && (
            <CircularProgress size={16} />
          )}
        </Box>
      </Box>

      <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
        Temperature ranges per day in {isFahrenheit ? 'Fahrenheit' : 'Celsius'}. Shows minimum and maximum values.
      </Typography>

      {/* Visual Color Legend */}
      <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: '600', mb: 1, display: 'block' }}>
          Temperature Scale:
        </Typography>
        <Box sx={{ position: 'relative', mb: 1 }}>
          {/* Color gradient bar */}
          <Box
            sx={{
              height: '20px',
              width: '100%',
              background: generateColorGradient(),
              borderRadius: '4px',
              border: '1px solid #ddd'
            }}
          />
          {/* Temperature labels */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: '500' }}>
              {convertTemp(-5)}{getTempUnit()}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: '500' }}>
              {convertTemp(10)}{getTempUnit()}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: '500' }}>
              {convertTemp(25)}{getTempUnit()}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: '500' }}>
              {convertTemp(40)}{getTempUnit()}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: '500' }}>
              {convertTemp(55)}{getTempUnit()}
            </Typography>
          </Box>
          {/* Range descriptions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#1565c0', fontSize: '10px' }}>
              Cold
            </Typography>
            <Typography variant="caption" sx={{ color: '#2e7d32', fontSize: '10px', position: 'absolute', left: '45%' }}>
              Normal Range
            </Typography>
            <Typography variant="caption" sx={{ color: '#d32f2f', fontSize: '10px' }}>
              Hot
            </Typography>
          </Box>
        </Box>
      </Box>
      

      {devices.length === 0 ? (
        <Typography>No devices found for this organization.</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e1f5fe' }}>
                  Device
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e1f5fe' }}>
                  Meter
                </TableCell>
                {sortedDates.map(date => (
                  <TableCell 
                    key={date} 
                    align="center" 
                    sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: '#e1f5fe',
                      minWidth: '100px',
                      fontSize: '12px'
                    }}
                  >
                    {date.split('-').slice(1).join('/')}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e1f5fe' }}>
                  Overall Range
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.sort((a, b) => {
                const serialA = String(a.serialnumber || '');
                const serialB = String(b.serialnumber || '');
                return serialA.localeCompare(serialB);
              }).map(device => {
                const deviceTemperature = temperatureData[device.id];
                const overallMin = deviceTemperature?.summary?.overallMin;
                const overallMax = deviceTemperature?.summary?.overallMax;
                
                return (
                  <TableRow key={device.id}>
                    <TableCell sx={{ fontWeight: '600', fontSize: '12px' }}>
                      {device.serialnumber}
                    </TableCell>
                    <TableCell sx={{ fontSize: '10px', color: '#666' }}>
                      {device.externalmeterid || 'N/A'}
                    </TableCell>
                    {sortedDates.map(date => {
                      const dayData = deviceTemperature?.dailyTemperature?.find(day => day.date === date);
                      const minTemp = dayData?.minTemperature;
                      const maxTemp = dayData?.maxTemperature;

                      return (
                        <TableCell
                          key={date}
                          align="center"
                          sx={{
                            backgroundColor: minTemp !== undefined ? '#fafafa' : '#fafafa',
                            fontSize: '10px',
                            fontWeight: '600',
                            padding: '4px',
                            verticalAlign: 'middle'
                          }}
                        >
                          {isEndpointLoading('temperature-daily', device.id) ? (
                            <CircularProgress size={12} />
                          ) : minTemp !== undefined && maxTemp !== undefined ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <div
                                style={{
                                  backgroundColor: getTemperatureColor(minTemp),
                                  color: getTemperatureTextColor(minTemp),
                                  padding: '2px 4px',
                                  borderRadius: '3px',
                                  fontSize: '11px'
                                }}
                              >
                                {convertTemp(minTemp)}{getTempUnit()}
                              </div>
                              <div
                                style={{
                                  backgroundColor: getTemperatureColor(maxTemp),
                                  color: getTemperatureTextColor(maxTemp),
                                  padding: '2px 4px',
                                  borderRadius: '3px',
                                  fontSize: '11px'
                                }}
                              >
                                {convertTemp(maxTemp)}{getTempUnit()}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#999' }}>-</span>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: '600',
                        backgroundColor: '#e3f2fd',
                        color: overallMin !== undefined ? '#1976d2' : '#999'
                      }}
                    >
                      {overallMin !== undefined && overallMax !== undefined ? (
                        <div>
                          <div style={{ fontSize: '11px' }}>{convertTemp(overallMin)}{getTempUnit()}</div>
                          <div style={{ fontSize: '9px', color: '#666' }}>{convertTemp(overallMax)}{getTempUnit()}</div>
                        </div>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Summary */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#e1f5fe', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: '600' }}>
          Summary: {devices.length} devices, {Object.keys(temperatureData).length} with temperature data, {sortedDates.length} days
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Format: Top value = min temperature, bottom value = max temperature per day. Each value has individual color coding based on its temperature. Unit: {isFahrenheit ? 'Fahrenheit' : 'Celsius'}
        </Typography>
      </Box>
    </WalteroCard>
  );
};

export default SimpleTemperatureTable;